import * as React from "react";

import { cn } from "@/lib/utils";
import { cva, VariantProps } from "class-variance-authority";
import { AnimatePresence, motion, Variants } from "motion/react";
import { Pencil } from "lucide-react";
import { useMemo, useState } from "react";

const iconMotion: Variants = {
  rest: {
    opacity: 0,
    rotate: -20,
    transition: {
      duration: 0.2,
      type: "tween",
      ease: "easeIn",
    },
  },
  hover: {
    opacity: 1,
    rotate: 0,
    transition: {
      duration: 0.2,
      type: "tween",
      ease: "easeOut",
    },
  },
  focus: {
    opacity: 1,
    rotate: 0,
    transition: {
      duration: 0.2,
      type: "tween",
      ease: "easeOut",
    },
  },
  active: {
    opacity: 1,
    rotate: 0,
    transition: {
      duration: 0.2,
      type: "tween",
      ease: "easeOut",
    },
  },
};

const inputVariants = cva(
  "flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
  {
    variants: {
      variant: {
        default: "",
        clickToEdit: "cursor-pointer bg-transparent border-none hover:border hover:border-input hover:bg-secondary",
      },
      inputSize: {
        sm: "h-9 text-sm",
        default: "h-12 text-lg md:text-base",
        md: "h-12 text-xl md:text-lg",
        lg: "h-14 text-2xl md:text-xl",
        xl: "h-16 text-3xl md:text-2xl",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "md",
    },
  },
);

export interface InputProps extends React.ComponentProps<"input">, VariantProps<typeof inputVariants> {
  floatingLabel?: string;
  pointerEvents?: "none" | "auto";
  name?: string;
  id?: string;
  showIcon?: boolean;
  wrapperClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      type,
      variant,
      inputSize,
      floatingLabel,
      pointerEvents,
      showIcon = true,
      name,
      id,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const showFloatingLabel = useMemo(() => {
      return floatingLabel && !isFocused && !isHovered;
    }, [floatingLabel, isFocused, isHovered]);

    const iconSpacing =
      variant === "clickToEdit" ? (inputSize === "lg" ? "pr-10" : inputSize === "xl" ? "pr-12" : "pr-3") : "pr-3";

    const iconOffset =
      variant === "clickToEdit"
        ? inputSize === "lg"
          ? "right-3"
          : inputSize === "xl"
            ? "right-4"
            : "right-3"
        : "right-3";

    const iconSize =
      variant === "clickToEdit"
        ? inputSize === "lg"
          ? "w-6 h-6"
          : inputSize === "xl"
            ? "w-7 h-7"
            : "w-4 h-4"
        : "w-4 h-4";

    return (
      <motion.div
        className={cn("relative flex flex-col items-start w-full gap-0", wrapperClassName)}
        whileHover="hover"
        whileFocus="focus"
        whileTap="active"
        initial="rest"
        animate={isFocused ? "focus" : "rest"}
        exit="rest"
      >
        <AnimatePresence>
          {showFloatingLabel && (
            <motion.span className={cn("text-base text-muted-foreground w-full", "z-10")}>{floatingLabel}</motion.span>
          )}
        </AnimatePresence>

        <input
          ref={ref}
          name={name}
          id={id}
          type={type}
          className={cn(inputVariants({ variant, inputSize, className }), iconSpacing)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && variant === "clickToEdit") {
              (e.target as HTMLInputElement).blur();
            }
          }}
          {...props}
        />
      </motion.div>
    );
  },
);
Input.displayName = "Input";

export { Input, inputVariants };
